import React from 'react';
import {
  AbsoluteFill,
  useCurrentFrame,
  useVideoConfig,
  interpolate,
  spring,
} from 'remotion';

const BG = '#0F1419';
const PRIMARY = '#10B981';
const SECONDARY = '#F97316';
const ACCENT = '#6366F1';
const BAD = '#EF4444';

const TOTAL = 540;

type RowIssue = 'null' | 'dup' | 'type' | 'none';

interface RowData {
  cells: string[];
  issues: RowIssue[];
}

const RAW_ROWS: RowData[] = [
  { cells: ['1', '张三', 'NULL', 'zhang@x.com'], issues: ['null'] },
  { cells: ['2', '张三', '28', 'zhang@y.com'], issues: ['dup'] },
  { cells: ['3', '张三', '28', 'zhang@z.com'], issues: ['dup'] },
  { cells: ['4', '李四', '"29"', 'li@x.com'], issues: ['type'] },
  { cells: ['5', 'NULL', '31', 'wu@x.com'], issues: ['null'] },
];

const CLEAN_ROWS: RowData[] = [
  { cells: ['2', '张三', '28', 'zhang@y.com'], issues: ['none'] },
  { cells: ['4', '李四', '29', 'li@x.com'], issues: ['none'] },
];

function phaseOpacity(frame: number, start: number, end: number, fade = 22): number {
  return interpolate(frame, [start, start + fade, end - fade, end], [0, 1, 1, 0], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
  });
}

const NoiseBg: React.FC = () => (
  <div
    style={{
      position: 'absolute',
      inset: 0,
      opacity: 0.035,
      backgroundImage: `
        repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(255,255,255,0.03) 2px, rgba(255,255,255,0.03) 3px),
        repeating-linear-gradient(90deg, transparent, transparent 2px, rgba(255,255,255,0.03) 2px, rgba(255,255,255,0.03) 3px)
      `,
    }}
  />
);

const PIPE_STEPS = [
  { key: 'raw', label: '原始数据', sub: '多源汇入' },
  { key: 'null', label: '去空值', sub: '删除无效行' },
  { key: 'dup', label: '去重', sub: '键合并' },
  { key: 'type', label: '类型转换', sub: '统一模式' },
  { key: 'ok', label: '质检通过', sub: '可入库' },
];

interface PipelineProps {
  frame: number;
  fps: number;
  activeIndex: number;
}

const PipelineRail: React.FC<PipelineProps> = ({ frame, fps, activeIndex }) => {
  const nodeW = 168;
  const gap = 28;
  const startX = 70;
  const y = 850;

  return (
    <svg viewBox="0 0 1920 1080" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: 'none' }}>
      <defs>
        <linearGradient id="pipeFlow" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor={PRIMARY} stopOpacity={0.3} />
          <stop offset="50%" stopColor={ACCENT} stopOpacity={0.9} />
          <stop offset="100%" stopColor={SECONDARY} stopOpacity={0.4} />
        </linearGradient>
      </defs>
      <path
        d={`M ${startX} ${y + 36} H ${startX + PIPE_STEPS.length * (nodeW + gap) - gap}`}
        stroke="rgba(255,255,255,0.08)"
        strokeWidth={4}
        strokeLinecap="round"
      />
      <path
        d={`M ${startX} ${y + 36} H ${startX + interpolate(frame, [60, 420], [0, PIPE_STEPS.length - 1], { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' }) * (nodeW + gap)}`}
        stroke="url(#pipeFlow)"
        strokeWidth={5}
        strokeLinecap="round"
      />
      {PIPE_STEPS.map((step, i) => {
        const x = startX + i * (nodeW + gap);
        const pulse = spring({
          frame: frame - 90 - i * 45,
          fps,
          config: { damping: 16, stiffness: 140 },
        });
        const lit = i <= activeIndex ? 0.85 + pulse * 0.15 : 0.25;
        const stroke = i === activeIndex ? PRIMARY : 'rgba(255,255,255,0.15)';
        return (
          <g key={step.key} transform={`translate(${x}, ${y})`}>
            <rect
              width={nodeW}
              height={80}
              rx={12}
              fill={`rgba(16,24,36,${0.75 + lit * 0.2})`}
              stroke={stroke}
              strokeWidth={i === activeIndex ? 2.5 : 1.5}
            />
            <text x={nodeW / 2} y={32} textAnchor="middle" fill={PRIMARY} fontSize={22} fontWeight={800} fontFamily="PingFang SC, sans-serif">
              {step.label}
            </text>
            <text x={nodeW / 2} y={64} textAnchor="middle" fill="rgba(255,255,255,0.38)" fontSize={20} fontFamily="PingFang SC, sans-serif">
              {step.sub}
            </text>
            {i < PIPE_STEPS.length - 1 && (
              <path
                d={`M ${nodeW + 6} 36 L ${nodeW + gap - 6} 36`}
                stroke={i < activeIndex ? ACCENT : 'rgba(255,255,255,0.12)'}
                strokeWidth={2}
              />
            )}
          </g>
        );
      })}
    </svg>
  );
};

interface TableViewProps {
  title: string;
  columns: string[];
  rows: RowData[];
  x: number;
  y: number;
  frame: number;
  mode: 'raw' | 'nulling' | 'dedupe' | 'typing' | 'clean';
}

const DataTableStage: React.FC<TableViewProps> = ({
  title,
  columns,
  rows,
  x,
  y,
  frame,
  mode,
}) => {
  const colW = [80, 150, 120, 280];
  const colGap = 10;
  const padX = 24;
  const totalW = colW.reduce((a, b) => a + b, 0) + colGap * (colW.length - 1) + padX * 2;
  const rowH = 52;
  const headerH = 80;

  const nullPhase = mode === 'nulling';
  const dupPhase = mode === 'dedupe';
  const typePhase = mode === 'typing';

  return (
    <g transform={`translate(${x}, ${y})`}>
      <rect
        width={totalW}
        height={headerH + rows.length * rowH + 50}
        rx={16}
        fill="rgba(10,14,22,0.92)"
        stroke={mode === 'clean' ? PRIMARY : 'rgba(255,255,255,0.12)'}
        strokeWidth={mode === 'clean' ? 2 : 1}
      />
      <text x={18} y={30} fill="rgba(255,255,255,0.45)" fontSize={20} fontFamily="PingFang SC, sans-serif">
        {title}
      </text>
      {columns.map((c, i) => {
        let ox = padX;
        for (let j = 0; j < i; j++) ox += colW[j] + colGap;
        return (
          <text
            key={c}
            x={ox + colW[i] / 2}
            y={headerH + 4}
            textAnchor="middle"
            fill={ACCENT}
            fontSize={22}
            fontWeight={700}
            fontFamily="ui-monospace, sans-serif"
          >
            {c}
          </text>
        );
      })}
      <line x1={10} y1={headerH + 12} x2={totalW - 10} y2={headerH + 12} stroke="rgba(255,255,255,0.1)" />

      {rows.map((row, ri) => {
        const hasNull = row.issues.includes('null');
        const rowDup = row.issues.includes('dup');
        const rowType = row.issues.includes('type');

        let opacity = 1;
        let dy = 0;
        let scaleX = 1;

        if (nullPhase && hasNull) {
          opacity = interpolate(frame, [175, 235], [1, 0.08], { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' });
        }

        if (dupPhase && rowDup) {
          const merge = interpolate(frame, [265, 320], [0, 1], { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' });
          if (ri === 1) {
            dy = merge * -rowH;
            opacity = 1;
          }
          if (ri === 2) {
            opacity = interpolate(frame, [285, 330], [1, 0], { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' });
            scaleX = interpolate(frame, [285, 320], [1, 0.2], { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' });
          }
        }

        return (
          <g
            key={ri}
            transform={`translate(0, ${headerH + 52 + ri * rowH + dy}) scale(${scaleX}, 1)`}
            opacity={opacity}
          >
            {row.cells.map((cell, ci) => {
              let ox = padX;
              for (let j = 0; j < ci; j++) ox += colW[j] + colGap;
              const isNullCell = cell === 'NULL';
              const isBadString = cell.includes('"');
              const showIssue =
                (isNullCell && (mode === 'raw' || nullPhase)) ||
                (rowDup && (mode === 'raw' || dupPhase)) ||
                (isBadString && (mode === 'raw' || typePhase));

              let fill = 'rgba(235,245,255,0.92)';
              if (isNullCell && showIssue) fill = BAD;
              if (rowDup && showIssue) fill = SECONDARY;
              if (isBadString && showIssue) fill = ACCENT;

              const converted =
                typePhase &&
                rowType &&
                interpolate(frame, [355, 400], [0, 1], { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' }) >
                  0.4;

              const display =
                converted && isBadString ? '29' : cell;

              return (
                <text
                  key={ci}
                  x={ox + colW[ci] / 2}
                  y={0}
                  textAnchor="middle"
                  fill={converted && isBadString ? PRIMARY : fill}
                  fontSize={20}
                  fontFamily="ui-monospace, PingFang SC, sans-serif"
                  fontWeight={converted && isBadString ? 700 : 400}
                >
                  {display}
                </text>
              );
            })}
          </g>
        );
      })}
    </g>
  );
};

const CheckColumn: React.FC<{ x: number; y: number; count: number; frame: number; fps: number }> = ({
  x,
  y,
  count,
  frame,
  fps,
}) => (
  <g transform={`translate(${x}, ${y})`}>
    {Array.from({ length: count }).map((_, i) => {
      const start = 458 + i * 14;
      const t = interpolate(frame, [start, start + 18], [0, 1], {
        extrapolateLeft: 'clamp',
        extrapolateRight: 'clamp',
      });
      const sc = spring({
        frame: frame - start,
        fps,
        config: { damping: 12, stiffness: 160 },
      });
      return (
        <g key={i} transform={`translate(0, ${i * 52})`} opacity={t}>
          <circle cx={16} cy={0} r={14} fill={`${PRIMARY}22`} stroke={PRIMARY} strokeWidth={2} />
          <g transform={`translate(16, 0) scale(${interpolate(sc, [0, 1], [0.2, 1])}) translate(-16, 0)`}>
            <path
              d="M 8 -2 L 14 6 L 26 -10"
              fill="none"
              stroke={PRIMARY}
              strokeWidth={2.5}
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </g>
        </g>
      );
    })}
  </g>
);

export const DataCleaningAnimation: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  const titleSpring = spring({ frame, fps, config: { damping: 16, stiffness: 105 } });

  const pRaw = phaseOpacity(frame, 48, 170);
  const pNull = phaseOpacity(frame, 155, 255);
  const pDup = phaseOpacity(frame, 240, 345);
  const pType = phaseOpacity(frame, 330, 435);
  const pClean = phaseOpacity(frame, 415, TOTAL);

  const activePipe =
    frame < 90
      ? 0
      : frame < 180
        ? 0
        : frame < 270
          ? 1
          : frame < 360
            ? 2
            : frame < 450
              ? 3
              : 4;

  const beforeAfter = spring({
    frame: frame - 440,
    fps,
    config: { damping: 14, stiffness: 100 },
  });

  const mode: TableViewProps['mode'] =
    pClean > 0.2
      ? 'clean'
      : pType > 0.15
        ? 'typing'
        : pDup > 0.15
          ? 'dedupe'
          : pNull > 0.15
            ? 'nulling'
            : 'raw';

  const showRawTable = pRaw > 0.05 || pNull > 0.05 || pDup > 0.05 || pType > 0.05 || pClean > 0.05;
  const tableOpacity = pClean > 0.2 ? 0.85 : Math.max(pRaw, pNull, pDup, pType);

  return (
    <AbsoluteFill
      style={{
        background: `radial-gradient(ellipse 90% 70% at 80% 20%, ${ACCENT}0f, transparent 55%), ${BG}`,
        fontFamily: 'system-ui, -apple-system, PingFang SC, sans-serif',
        overflow: 'hidden',
      }}
    >
      <NoiseBg />

      <div
        style={{
          position: 'absolute',
          top: 36,
          left: 64,
          opacity: titleSpring,
          transform: `translateY(${interpolate(titleSpring, [0, 1], [-26, 0])}px)`,
        }}
      >
        <h1
          style={{
            fontSize: 56,
            fontWeight: 800,
            margin: 0,
            background: `linear-gradient(105deg, ${PRIMARY}, ${SECONDARY}, ${ACCENT})`,
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
          }}
        >
          数据清洗全流程
        </h1>
        <p style={{ fontSize: 28, margin: '10px 0 0', color: 'rgba(255,255,255,0.4)' }}>
          发现问题 · 流水线处理 · 对比验收
        </p>
      </div>

      <div
        style={{
          position: 'absolute',
          left: 64,
          top: 180,
          maxWidth: 1300,
          opacity: interpolate(frame, [40, 72], [0, 1], { extrapolateRight: 'clamp' }),
        }}
      >
        <p style={{ margin: 0, fontSize: 24, lineHeight: 1.65, color: 'rgba(255,255,255,0.48)' }}>
          真实数据常含缺失值、重复键与类型不一致。通过可重复的清洗步骤，将原始表转化为可分析、可建模的干净视图。
        </p>
      </div>

      <div
        style={{
          position: 'absolute',
          right: 270,
          bottom: 30,
          width: 380,
          padding: '24px 26px',
          borderRadius: 18,
          opacity: spring({ frame: frame - 70, fps, config: { damping: 18, stiffness: 100 } }),
          background: 'linear-gradient(160deg, rgba(16,185,129,0.12), rgba(99,102,241,0.08))',
          border: '1px solid rgba(255,255,255,0.08)',
        }}
      >
        <div style={{ fontSize: 24, fontWeight: 800, color: SECONDARY, letterSpacing: 2, marginBottom: 14 }}>质量指标</div>
        {[
          { k: '完整率', v: interpolate(frame, [200, 380], [62, 96], { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' }) },
          { k: '唯一性', v: interpolate(frame, [280, 400], [55, 99], { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' }) },
          { k: '类型一致', v: interpolate(frame, [360, 500], [48, 100], { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' }) },
        ].map((m) => (
          <div key={m.k} style={{ marginBottom: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
              <span style={{ color: 'rgba(255,255,255,0.55)', fontSize: 22 }}>{m.k}</span>
              <span style={{ color: PRIMARY, fontWeight: 800, fontSize: 22 }}>{Math.round(m.v)}%</span>
            </div>
            <div style={{ height: 8, borderRadius: 4, background: 'rgba(255,255,255,0.06)' }}>
              <div
                style={{
                  height: '100%',
                  width: `${m.v}%`,
                  borderRadius: 4,
                  background: `linear-gradient(90deg, ${PRIMARY}, ${ACCENT})`,
                }}
              />
            </div>
          </div>
        ))}
      </div>

      <svg viewBox="0 0 1920 1080" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}>
        <defs>
          <filter id="tblGlow" x="-30%" y="-30%" width="160%" height="160%">
            <feGaussianBlur stdDeviation="8" result="b" />
            <feMerge>
              <feMergeNode in="b" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        {showRawTable && (
          <g opacity={tableOpacity} filter={pClean > 0.3 ? undefined : 'url(#tblGlow)'}>
            <DataTableStage
              title="raw_customers.csv"
              columns={['id', 'name', 'age', 'email']}
              rows={RAW_ROWS}
              x={120}
              y={288}
              frame={frame}
              mode={mode}
            />
          </g>
        )}

        {pClean > 0.12 && (
          <g opacity={pClean * beforeAfter}>
            <rect x={980} y={310} width={820} height={350} rx={20} fill="rgba(8,12,20,0.55)" stroke={`${PRIMARY}33`} />
            <text x={1010} y={360} fill={PRIMARY} fontSize={18} fontWeight={800} fontFamily="PingFang SC, sans-serif">
              清洗后 · 对比视图
            </text>
            <DataTableStage
              title="clean_customers (Parquet)"
              columns={['id', 'name', 'age', 'email']}
              rows={CLEAN_ROWS}
              x={1020}
              y={390}
              frame={frame}
              mode="clean"
            />
            <CheckColumn x={1740} y={512} count={2} frame={frame} fps={fps} />
            <text x={1180} y={700} fill="rgba(255,255,255,0.42)" fontSize={20} fontFamily="PingFang SC, sans-serif">
              左侧：原始问题高亮 · 右侧：规则化后的可信数据
            </text>
          </g>
        )}

        {pClean > 0.2 && (
          <g opacity={interpolate(pClean, [0.2, 0.5], [0, 1])}>
            <line x1={860} y1={480} x2={920} y2={480} stroke={ACCENT} strokeWidth={2} strokeDasharray="6 5" />
            <polygon points="920,480 880,465 880,495" fill={ACCENT} />
          </g>
        )}
      </svg>

      <PipelineRail frame={frame} fps={fps} activeIndex={activePipe} />

      <div
        style={{
          position: 'absolute',
          top: 700,
          left: 260,
          fontSize: 20,
          color: 'rgba(255,255,255,0.35)',
          opacity: interpolate(frame, [100, 140], [0, 1], { extrapolateRight: 'clamp' }),
        }}
      >
        <span style={{ color: BAD }}>红色</span>：空值 ·{' '}
        <span style={{ color: SECONDARY }}>橙色</span>：重复键 ·{' '}
        <span style={{ color: ACCENT }}>靛色</span>：类型异常
      </div>

      <div
        style={{
          position: 'absolute',
          bottom: 40,
          left: 70,
          fontSize: 24,
          color: 'rgba(255,255,255,0.28)',
        }}
      >
        {Math.floor(frame / fps)}s / {Math.floor(TOTAL / fps)}s · 30fps
      </div>
    </AbsoluteFill>
  );
};
